/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-account
 * @responsibility Compose independently tuned desktop and mobile SET-F02 Account pane surfaces.
 * @state-owner React transient edit/pending form state; parent owns server state
 * @allowed-imports React; shared translator; @vispo/contracts types
 * @forbidden direct API client; password UI or copy; email/provider adapter calls
 * @public-api AccountSettingsSurface
 * @side-effects invokes parent commands only
 * @tests AccountSettingsSurface.test.tsx
 */

import type { ProfileContext } from '@vispo/contracts';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import type { WorkspaceAuthProvider } from '../../../app/config/environment';
import { useTranslator } from '../../../shared/model/use-workspace';
import './settings-account.css';

const PROVIDER_LABEL: Record<WorkspaceAuthProvider, string> = {
  email: 'Email 驗證碼',
  google: 'Google',
};

// Mirrors the backend's own allowlist and cap (identity_access/service.py's
// _IMAGE_UPLOAD_MIME_TYPES / _IMAGE_UPLOAD_MAX_BYTES) so an obviously-invalid selection is
// rejected immediately, before any network round trip -- the server stays the authoritative
// validator (magic bytes, dimensions) for everything this cheap client-side check can't see.
const AVATAR_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

function firstGrapheme(value: string): string {
  return Array.from(value.trim())[0]?.toLocaleUpperCase() || 'V';
}

function isValidAvatarSelection(file: File): boolean {
  return AVATAR_MIME_TYPES.has(file.type) && file.size > 0 && file.size <= AVATAR_MAX_BYTES;
}

export interface AccountSettingsSurfaceProps {
  avatarUploadErrorCode: string | null;
  avatarUrl: string | null;
  email: string;
  emailChangeError: string | null;
  emailChangePending: boolean;
  emailChangeSending: boolean;
  errorCode: string | null;
  linkedProviders: readonly WorkspaceAuthProvider[];
  mobile: boolean;
  onClearAvatar(): void;
  onRequestEmailChange(newEmail: string): void;
  onResetAvatarUploadError(): void;
  onRetry(): void;
  onSave(displayName: string): void;
  onUploadAvatar(file: File): void;
  profile: ProfileContext;
  saving: boolean;
  uploadingAvatar: boolean;
}

export function AccountSettingsSurface(props: AccountSettingsSurfaceProps) {
  const t = useTranslator();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(props.profile.display_name);
  const [changingEmail, setChangingEmail] = useState(false);
  const [draftEmail, setDraftEmail] = useState('');
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [pendingAvatarPreviewUrl, setPendingAvatarPreviewUrl] = useState<string | null>(null);
  const [avatarSelectionInvalid, setAvatarSelectionInvalid] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const previousAvatarUrl = useRef(props.avatarUrl);

  const startEdit = () => {
    setDraftName(props.profile.display_name);
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const save = () => {
    props.onSave(draftName);
    setEditing(false);
  };
  const trimmedDraft = draftName.trim();
  const isDirty = trimmedDraft !== props.profile.display_name;
  const isValid = trimmedDraft.length >= 1 && trimmedDraft.length <= 120;
  const canSave = isDirty && isValid;

  useEffect(() => {
    if (props.emailChangePending) setChangingEmail(false);
  }, [props.emailChangePending]);

  const clearAvatarSelection = () => {
    if (pendingAvatarPreviewUrl) URL.revokeObjectURL(pendingAvatarPreviewUrl);
    setPendingAvatarFile(null);
    setPendingAvatarPreviewUrl(null);
    setAvatarSelectionInvalid(false);
  };

  // props.avatarUrl changing (for ANY reason -- a successful upload, or the separate
  // "移除頭像" clear-avatar action, both of which flow through their own independent mutation)
  // means server state has moved and the local pre-upload preview is now obsolete; release it.
  // A failed upload never changes avatarUrl, so this deliberately leaves the preview and the
  // selected file in place on error, letting the same picked file be retried without reselecting.
  // biome-ignore lint/correctness/useExhaustiveDependencies: clearAvatarSelection intentionally excluded -- it closes over pendingAvatarPreviewUrl, which changes on every file selection and would re-run this change-detection effect on selection alone, not just when avatarUrl itself changes
  useEffect(() => {
    if (previousAvatarUrl.current !== props.avatarUrl) {
      clearAvatarSelection();
    }
    previousAvatarUrl.current = props.avatarUrl;
  }, [props.avatarUrl]);

  const handleAvatarFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    props.onResetAvatarUploadError();
    if (!isValidAvatarSelection(file)) {
      clearAvatarSelection();
      setAvatarSelectionInvalid(true);
      return;
    }
    if (pendingAvatarPreviewUrl) URL.revokeObjectURL(pendingAvatarPreviewUrl);
    setAvatarSelectionInvalid(false);
    setPendingAvatarFile(file);
    setPendingAvatarPreviewUrl(URL.createObjectURL(file));
  };

  const cancelAvatarSelection = () => {
    clearAvatarSelection();
    props.onResetAvatarUploadError();
  };

  const confirmAvatarUpload = () => {
    if (pendingAvatarFile) props.onUploadAvatar(pendingAvatarFile);
  };

  return (
    <section
      aria-label={t('帳號')}
      className={`account-settings-pane${props.mobile ? ' account-settings-pane--mobile' : ' account-settings-pane--desktop'}`}
    >
      {props.errorCode === 'ACCOUNT_REVISION_CONFLICT' ? (
        <div className="account-settings-pane__error" role="alert">
          <span>{t('已被他人修改')}</span>
          <button type="button" onClick={props.onRetry}>
            {t('重新整理')}
          </button>
        </div>
      ) : props.errorCode ? (
        <div className="account-settings-pane__error" role="alert">
          <span>{t('更新失敗，請重試')}</span>
          <button type="button" onClick={props.onRetry}>
            {t('重新整理')}
          </button>
        </div>
      ) : null}

      <div className="account-settings-pane__identity">
        <span className="account-settings-pane__avatar">
          {pendingAvatarPreviewUrl ? (
            <img alt="" src={pendingAvatarPreviewUrl} />
          ) : props.avatarUrl ? (
            <img alt="" src={props.avatarUrl} />
          ) : (
            firstGrapheme(props.profile.display_name)
          )}
        </span>
        <div className="account-settings-pane__name">
          {editing ? (
            <>
              <input
                aria-label={t('姓名')}
                disabled={props.saving}
                maxLength={120}
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
              />
              <div className="account-settings-pane__actions">
                <button disabled={props.saving} type="button" onClick={cancelEdit}>
                  {t('取消')}
                </button>
                <button
                  className="account-settings-pane__primary"
                  disabled={!canSave || props.saving}
                  type="button"
                  onClick={save}
                >
                  {props.saving ? t('儲存中…') : t('儲存')}
                </button>
              </div>
            </>
          ) : (
            <>
              <b>{props.profile.display_name}</b>
              <button type="button" onClick={startEdit}>
                {t('編輯')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="account-settings-pane__avatar-upload">
        <input
          accept="image/png,image/jpeg,image/webp"
          aria-label={t('上傳頭像')}
          className="account-settings-pane__avatar-input"
          disabled={props.uploadingAvatar}
          ref={avatarInputRef}
          type="file"
          onChange={handleAvatarFileChange}
        />
        {pendingAvatarFile ? (
          <div className="account-settings-pane__actions">
            <button disabled={props.uploadingAvatar} type="button" onClick={cancelAvatarSelection}>
              {t('取消')}
            </button>
            <button
              className="account-settings-pane__primary"
              disabled={props.uploadingAvatar}
              type="button"
              onClick={confirmAvatarUpload}
            >
              {props.uploadingAvatar ? t('上傳中…') : t('上傳')}
            </button>
          </div>
        ) : null}
        {avatarSelectionInvalid || props.avatarUploadErrorCode ? (
          <p className="account-settings-pane__error" role="alert">
            {t('僅支援 PNG/JPEG/WEBP，上限 2 MB')}
          </p>
        ) : null}
      </div>

      {props.avatarUrl ? (
        <button
          className="account-settings-pane__clear-avatar"
          disabled={props.saving || props.uploadingAvatar}
          type="button"
          onClick={props.onClearAvatar}
        >
          {t('移除頭像')}
        </button>
      ) : null}

      <div className="account-settings-pane__section">
        <span className="account-settings-pane__label">Email</span>
        <p>{props.email}</p>
        {changingEmail ? (
          <div className="account-settings-pane__email-change">
            <input
              aria-label={t('新的 Email')}
              disabled={props.emailChangeSending}
              type="email"
              value={draftEmail}
              onChange={(event) => setDraftEmail(event.target.value)}
            />
            <div className="account-settings-pane__actions">
              <button
                disabled={props.emailChangeSending}
                type="button"
                onClick={() => setChangingEmail(false)}
              >
                {t('取消')}
              </button>
              <button
                className="account-settings-pane__primary"
                disabled={props.emailChangeSending || draftEmail.trim().length === 0}
                type="button"
                onClick={() => props.onRequestEmailChange(draftEmail.trim())}
              >
                {props.emailChangeSending ? t('傳送中…') : t('傳送確認信')}
              </button>
            </div>
            {props.emailChangeError ? (
              <p className="account-settings-pane__error" role="alert">
                {t('更新失敗，請重試')}
              </p>
            ) : null}
          </div>
        ) : props.emailChangePending ? (
          <p className="account-settings-pane__email-pending" role="status">
            {t('確認信已寄出，請完成信箱確認；變更將於確認後生效（最晚於下次登入時更新）。')}
          </p>
        ) : (
          <button type="button" onClick={() => setChangingEmail(true)}>
            {t('更改 Email')}
          </button>
        )}
      </div>

      <div className="account-settings-pane__section">
        <span className="account-settings-pane__label">{t('已連結的登入方式')}</span>
        <ul className="account-settings-pane__providers">
          {props.linkedProviders.map((provider) => (
            <li key={provider}>{t(PROVIDER_LABEL[provider])}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
